/** @jsxImportSource @emotion/react */

import {
  EuiButtonGroup,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiHorizontalRule,
  EuiSearchBar,
  EuiSpacer,
  EuiText,
} from "@inscopix/ideas-eui";
import "./ProjectCards.scss";
import { isDefined } from "utils/isDefined";
import { useEuiSearchBar } from "hooks/useEuiSearchBar";
import {
  ProjectCard,
  ProjectCardProps,
} from "components/ProjectCard/ProjectCard";
import { ButtonCreateProjectFilled } from "../ButtonCreateProject/ButtonCreateProjectFilled";
import { Project, Tenant } from "graphql/_Types";
import { Fragment, RefObject, useEffect, useMemo, useState } from "react";
import { orderBy as _orderBy } from "lodash";
import { useIdeasSearchParams } from "hooks/useIdeasSearchParams";
import { css } from "@emotion/react";
import { ToggleFilter, ToggleFilterProps } from "./ToggleFilter";
import { htmlIdGenerator } from "@inscopix/ideas-eui";
import { captureException } from "@sentry/react";
import {
  ProjectsGrid,
  ProjectsGridCellRendererParams,
  ProjectsGridProps,
} from "components/ProjectsTable/ProjectsGrid";
import { ProjectsCardActions } from "./ProjectsCardActions";
import { ProjectStatus } from "types/constants";
import { Virtualizer } from "virtua";

interface ProjectCardsProps {
  projects: (ProjectCardProps["project"] &
    Pick<Project, "dateCreated" | "dateLastActive">)[];
  tenantId?: Tenant["id"];
  virtuaScrollRef: RefObject<HTMLElement>;
}

const items = [
  { name: "Name", value: "name", first: "asc" },
  {
    name: "Date Created",
    value: "dateCreated",
    first: "desc",
  },
  {
    name: "Recent Activity",
    value: "recentActivity",
    first: "desc",
  },
  {
    name: "Active Storage",
    value: "activeStorage",
    first: "desc",
  },
  {
    name: "Archived Storage",
    value: "archivedStorage",
    first: "desc",
  },
  {
    name: "Total Storage",
    value: "totalStorage",
    first: "desc",
  },
  {
    name: "Compute Credits",
    value: "computeCredits",
    first: "desc",
  },
  {
    name: "Owner",
    value: "owner",
    first: "asc",
  },
  {
    name: "Organization",
    value: "organization",
    first: "asc",
  },
] as const;

const onSort = <
  T extends Pick<
    ProjectCardsProps["projects"][number],
    | "name"
    | "dateCreated"
    | "usedCredits"
    | "activeStorageSize"
    | "archivedStorageSize"
    | "dateLastActive"
    | "pinned"
    | "user"
    | "tenantName"
  >,
  U extends (typeof items)[number],
>(
  projects: T[],
  selectedItemKey: U["value"],
  selectedDir: "asc" | "desc",
) => {
  /**
   * Sort pinned first
   */
  const orderBy: typeof _orderBy = (
    projects: T[],
    comparators: ((project: T) => T[keyof T])[],
    orders: ("asc" | "desc")[],
  ) => {
    return _orderBy(
      projects,
      [(project) => project.pinned, ...comparators],
      ["desc", ...orders],
    );
  };

  switch (selectedItemKey) {
    case "name":
      switch (selectedDir) {
        case "asc":
          return orderBy(projects, [(project) => project.name], ["asc"]);
        case "desc":
          return orderBy(projects, [(project) => project.name], ["desc"]);
      }
      break;

    case "owner":
      switch (selectedDir) {
        case "asc":
          return orderBy(
            projects,
            [
              (project) =>
                (project.user?.first_name ?? "") +
                (project.user?.last_name ?? ""),
            ],
            ["asc"],
          );
        case "desc":
          return orderBy(
            projects,
            [
              (project) =>
                (project.user?.first_name ?? "") +
                (project.user?.last_name ?? ""),
            ],
            ["desc"],
          );
      }
      break;

    case "organization":
      switch (selectedDir) {
        case "asc":
          return orderBy(projects, [(project) => project.tenantName], ["asc"]);
        case "desc":
          return orderBy(projects, [(project) => project.tenantName], ["desc"]);
      }
      break;
    case "dateCreated":
      switch (selectedDir) {
        case "asc":
          return orderBy(projects, [(project) => project.dateCreated], ["asc"]);
        case "desc":
          return orderBy(
            projects,
            [(project) => project.dateCreated],
            ["desc"],
          );
      }
      break;
    case "recentActivity":
      switch (selectedDir) {
        case "asc":
          return orderBy(
            projects,
            [(project) => project.dateLastActive ?? project.dateCreated],
            ["asc"],
          );
        case "desc":
          return orderBy(
            projects,
            [(project) => project.dateLastActive ?? project.dateCreated],
            ["desc"],
          );
      }
      break;

    case "activeStorage":
      switch (selectedDir) {
        case "asc":
          return orderBy(
            projects,
            [(project) => Number(project.activeStorageSize ?? 0)],
            ["asc"],
          );
        case "desc":
          return orderBy(
            projects,
            [(project) => Number(project.activeStorageSize ?? 0)],
            ["desc"],
          );
      }
      break;

    case "archivedStorage":
      switch (selectedDir) {
        case "asc":
          return orderBy(
            projects,
            [(project) => Number(project.archivedStorageSize ?? 0)],
            ["asc"],
          );

        case "desc":
          return orderBy(
            projects,
            [(project) => Number(project.archivedStorageSize ?? 0)],
            ["desc"],
          );
      }
      break;

    case "computeCredits":
      switch (selectedDir) {
        case "asc":
          return orderBy(
            projects,
            [(project) => Number(project.usedCredits ?? 0)],
            ["asc"],
          );

        case "desc":
          return orderBy(
            projects,
            [(project) => Number(project.usedCredits ?? 0)],
            ["desc"],
          );
      }
      break;

    case "totalStorage":
      switch (selectedDir) {
        case "asc":
          return orderBy(
            projects,
            [
              (project) =>
                Number(project.archivedStorageSize ?? 0) +
                Number(project.activeStorageSize ?? 0),
            ],
            ["asc"],
          );

        case "desc":
          return orderBy(
            projects,
            [
              (project) =>
                Number(project.archivedStorageSize ?? 0) +
                Number(project.activeStorageSize ?? 0),
            ],
            ["desc"],
          );
      }
      break;
  }
  return projects;
};

const viewOptions = [
  {
    id: "CARDS",
    label: "Cards",
    iconType: "apmTrace",
    value: "CARDS",
  } as const,
  {
    id: "GRID",
    label: "Grid",
    iconType: "tableDensityCompact",
    value: "GRID",
  } as const,
];

/**
 * Displays a table of projects, allows selection and provides context menu for edit/deletion
 */
export const ProjectCards = ({
  projects,
  tenantId,
  virtuaScrollRef,
}: ProjectCardsProps) => {
  const [searchBarContainerId] = useState(
    htmlIdGenerator("search-bar-container")(),
  );
  const { setParam, getParam } = useIdeasSearchParams();
  const [view, setView] = useState<"GRID" | "CARDS">(() => {
    const viewParam = getParam("PROJECT_VIEW");
    if (viewParam === "grid") {
      return "GRID";
    }
    return "CARDS";
  });

  const { query, executeQuery, onChange, searchBarRef, roleFilter } =
    useEuiSearchBar();

  const [selectedOption, setSelectedOption] = useState<
    ToggleFilterProps<typeof items>["selected"]
  >({ item: "dateCreated", state: "desc" });

  const filteredProjects = useMemo(() => {
    const queriedProjects = isDefined(query)
      ? executeQuery(query, projects)
      : projects;

    return onSort(queriedProjects, selectedOption.item, selectedOption.state);
  }, [executeQuery, projects, query, selectedOption]);

  /**
   * Load sort route params into state on mount
   */
  useEffect(() => {
    const sortField = getParam("SORT_FIELD");
    const sortOrder = getParam("SORT_ORDER");
    if (sortField !== null && sortOrder !== null) {
      const selectedItem = items.find((item) => item.value === sortField);

      if (selectedItem !== undefined) {
        setSelectedOption({ item: selectedItem.value, state: sortOrder });
      } else {
        captureException("Failed to restore route params in Project cards");
      }
    }
  }, [getParam]);

  const filterItems = (() => {
    /**
     * Organization suppressed when viewing org page
     */
    if (tenantId !== undefined) {
      return items.filter((item) => item.value !== "organization");
    }
    return items;
  })();

  const gridActions: NonNullable<ProjectsGridProps["actions"]> = useMemo(() => {
    return {
      colId: "actions",
      csvValueGetter: null,
      headerName: "",
      width: 40,
      wrapHeaderText: true,
      resizable: false,
      sortable: false,
      pinned: "right",
      cellRenderer: ({ data }: ProjectsGridCellRendererParams) =>
        data !== undefined && (
          <ProjectsCardActions
            permissions={data.userPermissions}
            projectId={data.id}
            disabled={data.status === ProjectStatus.CLONING}
          />
        ),
    };
  }, []);

  /**
   * Hide organization column when showing single organization view
   */
  const gridSuppressColumns = useMemo(
    () => (tenantId !== undefined ? ["organization" as const] : []),
    [tenantId],
  );

  if (projects.length === 0) {
    return (
      <EuiEmptyPrompt
        title={<h2>Start your first project!</h2>}
        style={{ maxWidth: "100%" }}
        titleSize="xs"
        iconType="layers"
        body={
          <p>
            <EuiText>
              All data in IDEAS is contained within projects. Start your first
              project to begin uploading and analyzing data.
            </EuiText>
          </p>
        }
        actions={[
          <ButtonCreateProjectFilled
            key="create-project-button"
            tenantId={tenantId}
          />,
        ]}
      />
    );
  }

  return (
    <EuiFlexGroup direction="column" gutterSize="none">
      <EuiFlexGroup
        gutterSize="none"
        responsive={false}
        alignItems="center"
        // don't let the search bar grow vertically
        css={css`
          flex-grow: 0;
          #${searchBarContainerId} {
            .euiButtonEmpty {
              min-width: 70px;
              .euiFilterButton__notification {
                margin-right: 5px;
              }
              .euiButtonEmpty__content {
                display: flex;
                align-items: center;
                justify-content: center;
              }
            }
          }
        `}
      >
        <EuiFlexItem
          id={searchBarContainerId}
          css={
            // EuiSearchBar wraps the filter button at smaller widths, so we need to
            // prevent that to keep the search bar, filter button, and sort on the same line
            css`
              > .euiFlexGroup {
                flex-wrap: nowrap;
              }
            `
          }
        >
          <EuiSearchBar
            ref={searchBarRef}
            query={query}
            onChange={onChange}
            filters={[roleFilter]}
            box={{ placeholder: "Find a project...", incremental: true }}
          />
        </EuiFlexItem>

        {view === "CARDS" && (
          <EuiFlexItem
            grow={false}
            css={css`
              margin-left: 12px;
            `}
          >
            <ToggleFilter
              items={filterItems}
              selected={selectedOption}
              onChange={(newSelectedOption) => {
                setParam("SORT_FIELD", newSelectedOption.item);
                setParam("SORT_ORDER", newSelectedOption.state);
                setSelectedOption(newSelectedOption);
              }}
            />
          </EuiFlexItem>
        )}

        <EuiFlexItem
          grow={false}
          css={css`
            margin-left: 12px;
          `}
        >
          <EuiButtonGroup
            type="single"
            legend="View"
            buttonSize="m"
            options={viewOptions}
            idSelected={view}
            onChange={(id) => {
              const paramKey =
                (id as (typeof viewOptions)[number]["id"]) === "CARDS"
                  ? "cards"
                  : "grid";
              setParam("PROJECT_VIEW", paramKey);
              setView(id as (typeof viewOptions)[number]["id"]);
            }}
          />
        </EuiFlexItem>
      </EuiFlexGroup>
      <EuiSpacer size="xs" />
      <EuiFlexGroup direction="column" gutterSize="s">
        {view === "GRID" ? (
          <EuiFlexItem grow>
            <ProjectsGrid
              projects={filteredProjects}
              actions={gridActions}
              suppressColumns={gridSuppressColumns}
              suppressSortComparator
            />
          </EuiFlexItem>
        ) : (
          <Virtualizer startMargin={200} scrollRef={virtuaScrollRef}>
            {filteredProjects.map((project, idx, arr) => {
              const isLastProject = idx === arr.length - 1;
              const isNextProjectPinned = !isLastProject && arr[idx + 1].pinned;
              return (
                <Fragment key={project.id}>
                  {idx === 0 && (
                    <>
                      <EuiSpacer size="l" />
                    </>
                  )}

                  {project.pinned && !isLastProject && !isNextProjectPinned ? (
                    <>
                      <ProjectCard key={project.id} project={project} />
                      <EuiHorizontalRule />
                    </>
                  ) : (
                    <>
                      <ProjectCard key={project.id} project={project} />
                      {!isLastProject && <EuiSpacer size="m" />}
                    </>
                  )}
                </Fragment>
              );
            })}
          </Virtualizer>
        )}
      </EuiFlexGroup>
    </EuiFlexGroup>
  );
};
